import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import ResourceForm from '../../../Components/ResourceForm'
import FormFieldController from '../../../Components/FormFieldController'
import BackLink from '../../../Components/BackLink'
import CurrentImagePreview from '../../../Components/CurrentImagePreview'
import Button from '../../../Components/Button'
import { menuItemSchema } from '../../../schemas'
import { formatPrice } from '../../../utils/formatters'

function AddOnRow({ addOn, menuItemId, onEdit, editing, onCancelEdit }) {
  const [name, setName] = useState(addOn.name)
  const [pricePence, setPricePence] = useState(addOn.price_pence)
  const [active, setActive] = useState(addOn.active)

  const handleSave = () => {
    router.patch(`/manage/menu_items/${menuItemId}/menu_item_add_ons/${addOn.id}`, {
      menu_item_add_on: { name, price_pence: pricePence, active }
    })
    onCancelEdit()
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this add-on?')) {
      router.delete(`/manage/menu_items/${menuItemId}/menu_item_add_ons/${addOn.id}`)
    }
  }

  if (editing) {
    return (
      <tr className="bg-sky-50">
        <td className="px-4 py-2">
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            className="w-full rounded border-gray-300 text-sm" aria-label="Add-on name" />
        </td>
        <td className="px-4 py-2">
          <input type="number" value={pricePence} onChange={e => setPricePence(Number(e.target.value))}
            className="w-24 rounded border-gray-300 text-sm" aria-label="Add-on price in pence" />
        </td>
        <td className="px-4 py-2">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} aria-label="Add-on active" />
        </td>
        <td className="px-4 py-2 space-x-2">
          <Button link onClick={handleSave}>Save</Button>
          <Button link variant="muted" onClick={onCancelEdit}>Cancel</Button>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td className="px-4 py-3 font-medium text-gray-900">{addOn.name}</td>
      <td className="px-4 py-3 text-gray-900">{formatPrice(addOn.price_pence)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
          addOn.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {addOn.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 space-x-2">
        <Button link onClick={() => onEdit(addOn.id)}>Edit</Button>
        <Button link variant="danger" onClick={handleDelete}>Delete</Button>
      </td>
    </tr>
  )
}

function AddOnForm({ menuItemId }) {
  const [name, setName] = useState('')
  const [pricePence, setPricePence] = useState(0)
  const [showForm, setShowForm] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    router.post(`/manage/menu_items/${menuItemId}/menu_item_add_ons`, {
      menu_item_add_on: { name, price_pence: pricePence, active: true }
    })
    setName('')
    setPricePence(0)
    setShowForm(false)
  }

  if (!showForm) {
    return (
      <Button variant="secondary" onClick={() => setShowForm(true)}>
        + Add Add-on
      </Button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} required
            className="w-full rounded border-gray-300 text-sm" placeholder="e.g., Add chips" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price (pence)</label>
          <input type="number" value={pricePence} onChange={e => setPricePence(Number(e.target.value))} min="0"
            className="w-full rounded border-gray-300 text-sm" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit">Add</Button>
        <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
      </div>
    </form>
  )
}

export default function Edit({
  account,
  accounts,
  navigation,
  menu_item,
  add_ons = [],
  categories,
  sittings
}) {
  const [editingId, setEditingId] = useState(null)

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Edit: ${menu_item.name}`}
    >
      <BackLink href="/manage/menu_items">Back to Menu Items</BackLink>

      <div className="bg-white shadow rounded-lg">
        <ResourceForm
          schema={menuItemSchema}
          resource="menu_item"
          endpoint={`/manage/menu_items/${menu_item.id}`}
          method="patch"
          backLink="/manage/menu_items"
          submitLabel="Save Changes"
          defaultValues={{
            name: menu_item.name || '',
            description: menu_item.description || '',
            price_pence: menu_item.price_pence || '',
            category: menu_item.category || '',
            sittings: menu_item.sittings || [],
            visible: menu_item.visible ?? true,
            popular: menu_item.popular ?? false,
            image: null
          }}
        >
          {({ control, errors }) => (
            <>
              <FormFieldController
                control={control}
                name="name"
                label="Name"
                required
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="description"
                label="Description"
                type="textarea"
                rows={2}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="price_pence"
                label="Price"
                type="currency"
                required
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="category"
                label="Category"
                type="select"
                options={categories}
                required
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="sittings"
                label="Sittings"
                type="checkboxGroup"
                options={sittings}
                required
                hint="Select which meal periods this item is available"
                errors={errors}
              />

              <CurrentImagePreview
                url={menu_item.image_url}
                alt={menu_item.name}
              />

              <FormFieldController
                control={control}
                name="image"
                label={menu_item.image_url ? "Replace Image" : "Image"}
                type="file"
                accept="image/*"
                hint={menu_item.image_url ? "Leave empty to keep current image" : undefined}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="visible"
                label="Visible on menu"
                type="checkbox"
                hint="Uncheck to hide this item from the customer menu"
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="popular"
                label="Mark as Popular"
                type="checkbox"
                hint="Shows a 'Popular' badge on this item in the guest menu"
                errors={errors}
              />
            </>
          )}
        </ResourceForm>
      </div>

      {/* Add-ons Section */}
      <div className="bg-white shadow rounded-lg mt-6 p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Add-ons</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Optional extras guests can add to this item, each with its own price (e.g. &ldquo;Add chips&rdquo; +£1).
        </p>

        {add_ons.length > 0 && (
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {add_ons.map(addOn => (
                  <AddOnRow
                    key={addOn.id}
                    addOn={addOn}
                    menuItemId={menu_item.id}
                    editing={editingId === addOn.id}
                    onEdit={setEditingId}
                    onCancelEdit={() => setEditingId(null)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddOnForm menuItemId={menu_item.id} />
      </div>
    </Layout>
  )
}
